/**
 * Le socle sur la nuit — ce que la nuit repointe pour TOUS les univers (Brand core V16, « Clair et
 * sombre, au choix »). Le pendant de `base.ts` : un thème sombre l'étale, puis pose son univers.
 *
 * Le sombre n'est pas un second jeu de composants : c'est le MÊME code, lisant les mêmes jetons
 * sémantiques, avec un second jeu de valeurs. Trois mouvements expliquent tout ce qui suit :
 *
 *  1. **Le socle s'inverse.** Le marine écrit les titres et peint les icônes ; sur la nuit il ne
 *     peut être ni l'un ni l'autre (« Marine sur clair, blanc sur nuit », chap. 05). `primary`
 *     et `info` passent au blanc, `text-on-primary` au marine : un titre, une icône, une chip
 *     « Terminé » suivent sans qu'aucun composant ne le sache. L'inversion vaut pour ce que le
 *     livre dessine — un poussoir, une chip, un glyphe — et pas pour une grande surface, qui
 *     deviendrait un trou de lumière : celle-là prend `brand-fill`, le marine qui ne s'inverse pas.
 *     Ce que devient l'UNIVERS (`role`, son bouton) n'est pas ici : c'est l'affaire de chaque
 *     thème sombre — celui du parent s'inverse avec le marine, celui de l'élève ne bouge pas.
 *  2. **L'aplat d'une couleur, lui, ne s'inverse pas.** Le vert de progression, l'orange
 *     d'alerte, le rouge d'erreur, le jaune signature, les couleurs de champion et le dégradé des
 *     temps forts gardent leur valeur : ce sont des couleurs de marque, pas des tons de fond.
 *     Ce qui bouge autour d'elles, c'est leur TINTE (elle s'aplatit sur la nuit) et leur ENCRE
 *     (elle s'éclaircit au lieu de s'assombrir). Le blanc posé sur un aplat de couleur reste
 *     blanc : c'est le jeton `white`.
 *  3. **La profondeur change de moyen.** En clair, une carte blanche se détache de la page grise
 *     par son ombre. Sur la nuit, une ombre ne se voit pas : la carte se lit à sa SURFACE
 *     (`#152452` sur `#0B1533`). L'ombre se peint donc en noir, où elle disparaît, et la lueur
 *     d'accueil n'est pas montée — « en sombre, le fond reste uni : nuit » (chap. 03).
 *
 * Les valeurs et leur dérivation sont dans `tokens/colors.ts` → `night`.
 */
import { brand, legacy, night } from "./tokens/colors"

export const nightBaseColors = {
    // 1 — le socle s'inverse : l'encre et l'aplat marine passent au blanc.
    primary: brand.white,
    "primary-strong": brand.white,
    "primary-soft": night.raised,
    info: brand.white,
    infoLight: night.raised,
    "text-on-primary": brand.marine,
    "relief-primary": night.relief,
    // Le secondaire garde sa face blanche et son contour `#D5D9E6` (`border-secondary`, non
    // repointé) ; seul son relief passe au gris des boutons blancs de la nuit.
    "relief-secondary": night.relief,

    // 2 — les couleurs pleines ne bougent pas ; leur tinte s'aplatit sur la nuit et leur
    // encre s'éclaircit au lieu de s'assombrir. `brand-fill` est de celles-là, et c'est toute
    // sa raison d'être : le marine en grande SURFACE reste marine — absent d'ici à dessein.
    successLight: night.progressTint,
    "success-strong": night.progressInk,
    warningLight: night.alertTint,
    "warning-strong": night.alertInk,
    dangerLight: night.errorTint,
    "danger-strong": night.errorInk,
    // La chip de gain de l'élève : son corail ne bouge pas, sa tinte et son encre, si.
    "role-student-tint": night.roleStudentTint,
    "role-student-tint-ink": night.roleStudentInk,

    // 3 — les surfaces. La lueur d'accueil n'a pas de jeton à repointer : sur la nuit, `<Glow>`
    // n'est pas montée du tout (une couleur d'arrêt transparente ne s'éteint pas sur iOS).
    surface: night.page,
    "surface-raised": night.surface,
    "surface-subtle": night.raised,
    // Ce qui flotte vaut la carte, comme en clair : l'élève et le tuteur le montent d'une marche
    // (studentDark, tutorDark).
    "surface-floating": night.surface,
    // Le liseré clair du bord haut d'une feuille qui monte du bas : sur la nuit, la surface de la
    // feuille ne se détache plus du voile (1,35 à 1,37:1) ; le contour de la nuit, si (4:1 et plus).
    // L'élève, puis le parent (Wajih, 20/10).
    "sheet-edge": night.border,
    // Le contour de la main levée : sur la nuit, le jaune se détache seul.
    "raised-ring": legacy.transparent,
    "initials-fill": night.raised,
    // Le trait des squelettes passe au blanc : le jaune y virait au gris, puis à l'olive.
    "skeleton-sweep": brand.white,
    shadow: brand.black,
    backdrop: night.scrim,
    scrim: night.scrim,

    // Textes et filets.
    "text-primary": brand.white,
    "text-strong": brand.white,
    "text-muted": night.muted,
    border: night.border,
    "border-subtle": night.borderSubtle,
    "border-card": night.borderCard,

    // Les matières, éclaircies : elles viennent du seeder backend et plusieurs sont trop
    // foncées pour la nuit (`#000080`, `#8B0000`).
    "subject-maths": night.subjectMaths,
    "subject-physics": night.subjectPhysics,
    "subject-biology": night.subjectBiology,
    "subject-french": night.subjectFrench,
    "subject-history": night.subjectHistory,
    "subject-english": night.subjectEnglish,
    "subject-spanish": night.subjectSpanish,
    "subject-german": night.subjectGerman,
    "subject-portuguese": night.subjectPortuguese,
    "subject-philosophy": night.subjectPhilosophy,
    "subject-economics": night.subjectEconomics,
    "subject-emc": night.subjectEmc,
    "subject-italian": night.subjectItalian,
    "subject-default": night.subjectDefault,
} as const
